import { useCallback, useEffect, useRef, useState } from "react";
import { readableError } from "./pb.ts";

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  /** True only on the first load, so refreshes never flash skeletons. */
  loading: boolean;
  refreshing: boolean;
  reload: () => void;
  /** Apply an optimistic local change without waiting for a round trip. */
  patch: (next: (current: T) => T) => void;
}

export function useAsync<T>(load: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const alive = useRef(true);
  const loadRef = useRef(load);
  loadRef.current = load;

  const run = useCallback(async (isFirst: boolean) => {
    if (isFirst) setLoading(true);
    else setRefreshing(true);

    try {
      const result = await loadRef.current();
      if (!alive.current) return;
      setData(result);
      setError(null);
    } catch (cause) {
      if (!alive.current) return;
      setError(readableError(cause));
    } finally {
      if (alive.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void run(true);
    return () => {
      alive.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const reload = useCallback(() => void run(false), [run]);

  const patch = useCallback((next: (current: T) => T) => {
    setData((current) => (current === null ? current : next(current)));
  }, []);

  return { data, error, loading, refreshing, reload, patch };
}

/**
 * Debounced value, for text the user is still typing.
 *
 * The trigger version below debounces a call; this debounces a dependency, so a
 * search box can drive a `useAsync` without firing a query per keystroke.
 */
export function useDebouncedValue<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);

  return debounced;
}

/** Debounced trigger, so a burst of realtime events causes one refetch. */
export function useDebounced(fn: () => void, ms = 600): () => void {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fnRef.current(), ms);
  }, [ms]);
}
